import { createStore, createEvent, createEffect, sample, combine } from "effector";
import { contractorsApi } from "../api/contractors";
import { setTenantHeader } from "../api";
import { ContractorTenantAccess, TenantOut } from "../api/types";

const STORAGE_KEY = "activeTenantId";

export type ActiveTenantContext = {
  tenant_id: string;
  tenant_name?: string | null;
  tenant_schema?: string | null;
  object_kind?: string | null;
  data_available_from?: string | null;
  access_level?: "read" | "write" | null;
};

const readStoredTenantId = () => {
  if (typeof window === "undefined") {
    return null;
  }
  return window.localStorage.getItem(STORAGE_KEY);
};

const initialTenantId = readStoredTenantId();
if (initialTenantId) {
  setTenantHeader(initialTenantId);
}

export const setActiveTenantId = createEvent<string | null>();
export const clearTenantContext = createEvent();

export const fetchContractorTenantsFx = createEffect(async () => {
  const data = await contractorsApi.listMyTenants();
  return Array.isArray(data) ? data : [];
});

export const fetchAdminTenantsFx = createEffect(async () => {
  const data = await contractorsApi.listTenants();
  return Array.isArray(data) ? data : [];
});

export const $contractorTenants = createStore<ContractorTenantAccess[]>([])
  .on(fetchContractorTenantsFx.doneData, (_, data) => data)
  .reset(clearTenantContext);

export const $adminTenants = createStore<TenantOut[]>([])
  .on(fetchAdminTenantsFx.doneData, (_, data) => data)
  .reset(clearTenantContext);

export const $activeTenantId = createStore<string | null>(initialTenantId)
  .on(setActiveTenantId, (_, id) => (id ? String(id) : null))
  .reset(clearTenantContext);

$activeTenantId.watch((id) => {
  if (typeof window === "undefined") {
    return;
  }
  if (id) {
    window.localStorage.setItem(STORAGE_KEY, String(id));
  } else {
    window.localStorage.removeItem(STORAGE_KEY);
  }
  setTenantHeader(id);
});

export const $activeTenant = combine(
  $activeTenantId,
  $contractorTenants,
  $adminTenants,
  (activeId, contractorList, adminList): ActiveTenantContext | null => {
    if (!activeId) return null;

    const contractor = contractorList.find(
      (item) => String(item.tenant_id) === String(activeId)
    );
    if (contractor) {
      return {
        tenant_id: String(contractor.tenant_id),
        tenant_name: contractor.tenant_name,
        tenant_schema: contractor.tenant_schema,
        object_kind: contractor.object_kind,
        data_available_from: contractor.data_available_from,
        access_level: contractor.access_level || "read",
      };
    }

    const admin = adminList.find((item) => String(item.id) === String(activeId));
    if (admin) {
      return {
        tenant_id: String(admin.id),
        tenant_name: admin.name,
        tenant_schema: admin.schema_name,
        object_kind: admin.object_kind,
        data_available_from: admin.usage_start_date || null,
        access_level: "write",
      };
    }

    return null;
  }
);

export const $activeTenantAccessLevel = $activeTenant.map(
  (item) => item?.access_level || null
);

sample({
  clock: fetchContractorTenantsFx.doneData,
  source: $activeTenantId,
  fn: (activeId, list) => {
    if (!list.length) {
      return null;
    }
    const exists = list.some((item) => String(item.tenant_id) === String(activeId));
    return exists ? activeId : String(list[0].tenant_id);
  },
  target: setActiveTenantId,
});

sample({
  clock: fetchAdminTenantsFx.doneData,
  source: $activeTenantId,
  fn: (activeId, list) => {
    if (!list.length) {
      return null;
    }
    const exists = list.some((item) => String(item.id) === String(activeId));
    return exists ? activeId : String(list[0].id);
  },
  target: setActiveTenantId,
});
